import React from 'react'
import TeamLogo from './TeamLogo'
import { CheckCircle, XCircle, Minus, Clock } from 'lucide-react'

/**
 * Rich fragment components for structured AI response rendering
 * These provide polished UI for scorecards, stats tables, and props results
 */

// Scorecard - Game score header with team logos
export function ScorecardFragment({ fragment }) {
  const { homeTeam, awayTeam, homeScore, awayScore, status } = fragment
  const homeWinning = homeScore > awayScore
  const awayWinning = awayScore > homeScore
  
  return (
    <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl p-4 mb-3 border border-gray-100">
      <div className="flex items-center justify-between">
        {/* Away Team */}
        <div className="flex items-center gap-3">
          <TeamLogo code={awayTeam} name={awayTeam} size={32} />
          <div className="flex flex-col">
            <span className={`text-sm font-semibold ${awayWinning ? 'text-gray-900' : 'text-gray-600'}`}>
              {awayTeam}
            </span>
            <span className="text-[10px] text-gray-400">AWAY</span>
          </div>
          <span className={`text-2xl font-bold ml-2 ${awayWinning ? 'text-gray-900' : 'text-gray-500'}`}>
            {awayScore}
          </span>
        </div>
        
        {/* Status */}
        <div className="flex flex-col items-center">
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
            status === 'FINAL' ? 'bg-gray-200 text-gray-600' : 'bg-rose-100 text-rose-600'
          }`}>
            {status}
          </span>
        </div>
        
        {/* Home Team */}
        <div className="flex items-center gap-3">
          <span className={`text-2xl font-bold mr-2 ${homeWinning ? 'text-gray-900' : 'text-gray-500'}`}>
            {homeScore}
          </span>
          <div className="flex flex-col items-end">
            <span className={`text-sm font-semibold ${homeWinning ? 'text-gray-900' : 'text-gray-600'}`}>
              {homeTeam}
            </span>
            <span className="text-[10px] text-gray-400">HOME</span>
          </div>
          <TeamLogo code={homeTeam} name={homeTeam} size={32} />
        </div>
      </div>
    </div>
  )
}

// Top Performers - Player stats cards
export function TopPerformersFragment({ fragment }) {
  const { players } = fragment
  
  if (!players || players.length === 0) return null
  
  return (
    <div className="mb-3">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Top Performers
      </div>
      <div className="grid grid-cols-2 gap-2">
        {players.map((player, idx) => (
          <div 
            key={idx}
            className="bg-white rounded-lg p-3 border border-gray-100 shadow-sm"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-semibold text-gray-900 truncate">
                {player.name}
              </span>
              <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                {player.team}
              </span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="font-bold text-rose-500">{player.points} PTS</span>
              <span className="text-gray-500">{player.rebounds} REB</span>
              <span className="text-gray-500">{player.assists} AST</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Props Results - Betting props with hit/miss indicators
export function PropsResultsFragment({ fragment }) {
  const { props } = fragment
  
  if (!props || props.length === 0) return null
  
  const getResultIcon = (result) => {
    switch (result) {
      case 'hit':
        return <CheckCircle size={14} className="text-emerald-500" />
      case 'miss':
        return <XCircle size={14} className="text-rose-500" />
      case 'push':
        return <Minus size={14} className="text-gray-400" />
      default:
        return <Clock size={14} className="text-gray-300" />
    }
  }
  
  const getResultBg = (result) => {
    switch (result) {
      case 'hit':
        return 'bg-emerald-50 border-emerald-100'
      case 'miss':
        return 'bg-rose-50 border-rose-100'
      case 'push':
        return 'bg-gray-50 border-gray-100'
      default:
        return 'bg-white border-gray-100'
    }
  }
  
  return (
    <div className="mb-3">
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        Props Results
      </div>
      <div className="space-y-1.5">
        {props.map((prop, idx) => (
          <div 
            key={idx}
            className={`flex items-center justify-between p-2.5 rounded-lg border ${getResultBg(prop.result)}`}
          >
            <div className="flex items-center gap-2">
              {getResultIcon(prop.result)}
              <span className="text-sm font-medium text-gray-900">{prop.playerName}</span>
              <span className="text-xs text-gray-500">{prop.propType}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <span className="text-gray-400">Line: {prop.line}</span>
              {prop.actual !== null && (
                <span className={`font-semibold ${
                  prop.result === 'hit' ? 'text-emerald-600' : 
                  prop.result === 'miss' ? 'text-rose-600' : 'text-gray-600'
                }`}>
                  Actual: {prop.actual}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Generic Table - For any tabular data
export function TableFragment({ fragment }) {
  const { title, columns, rows } = fragment
  
  if (!columns || !rows || rows.length === 0) return null
  
  return (
    <div className="mb-3">
      {title && (
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          {title}
        </div>
      )}
      <div className="overflow-x-auto rounded-lg border border-gray-100">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              {columns.map((col, idx) => (
                <th key={idx} className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                {columns.map((col, colIdx) => (
                  <td key={colIdx} className="px-3 py-2 text-gray-700">
                    {row[col] ?? row[col.toLowerCase()] ?? '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Main fragment renderer - switches based on fragment type
export function RichFragmentRenderer({ fragments }) {
  if (!fragments || fragments.length === 0) return null
  
  return (
    <div className="rich-fragments">
      {fragments.map((fragment, idx) => {
        switch (fragment.type) {
          case 'scorecard':
            return <ScorecardFragment key={idx} fragment={fragment} />
          case 'topPerformers':
            return <TopPerformersFragment key={idx} fragment={fragment} />
          case 'propsResults':
            return <PropsResultsFragment key={idx} fragment={fragment} />
          case 'table':
            return <TableFragment key={idx} fragment={fragment} />
          default:
            return null
        }
      })}
    </div>
  )
}

export default RichFragmentRenderer
